import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ChatSimulatorModal from '@/components/chat/ChatSimulatorModal';
import { FileText, MessageSquareDashed } from 'lucide-react';
import type { Overview, PatientDetail } from './types';

type EmoviBotTabContentProps = {
  patient: PatientDetail;
  localPatient: PatientDetail;
  overview: Overview;
  translatedFlags: string[];
  isDev: boolean;
  isRegeneratingPrompt: boolean;
  onRegeneratePrompt: () => void;
  onOpenPromptModal: () => void;
};

export default function EmoviBotTabContent({
  patient,
  localPatient,
  overview,
  translatedFlags,
  isDev,
  isRegeneratingPrompt,
  onRegeneratePrompt,
  onOpenPromptModal,
}: EmoviBotTabContentProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Simulazione EmoviBot</CardTitle>
        <CardDescription>
          Prova in prima persona come il bot interagisce con {overview.hero.first_name}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
          <div className="space-y-3">
            <div className="rounded-lg border border-border/70 bg-muted/30 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  EB
                </span>
                EmoviBot
              </div>
              <p className="text-sm text-foreground">
                &quot;Ciao {overview.hero.first_name}, possiamo rivedere insieme come sta andando la
                settimana?&quot;
              </p>
              <p className="text-sm text-muted-foreground">
                &quot;Sono qui per ascoltarti. Possiamo partire da quello che ti pesa di più
                oggi.&quot;
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Questa simulazione aiuta a verificare tono, empatia e coerenza clinica prima di usare
              il bot con il cliente reale.
            </p>
            <ChatSimulatorModal
              patientId={localPatient.id}
              patientName={overview.hero.display_name}
              trigger={
                <Button size="sm" className="gap-2">
                  <MessageSquareDashed className="w-4 h-4" />
                  Avvia simulazione
                </Button>
              }
            />
          </div>
          <div className="rounded-lg border border-border/70 bg-background p-4 space-y-3">
            <p className="text-sm text-muted-foreground">Configurazione attuale</p>
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {patient.system_prompt || 'Nessun prompt configurato'}
            </p>
            <p className="text-xs text-muted-foreground">
              Ultimo aggiornamento:{' '}
              {overview.ai_config.last_updated_at
                ? new Date(overview.ai_config.last_updated_at).toLocaleString('it-IT')
                : '—'}
            </p>
            {translatedFlags.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Tag configurazione: {translatedFlags.join(', ')}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={onRegeneratePrompt}
                disabled={isRegeneratingPrompt}
              >
                {isRegeneratingPrompt ? 'Rigenerazione…' : 'Rigenera prompt AI'}
              </Button>
              {isDev && (
                <Button variant="secondary" size="sm" className="gap-2" onClick={onOpenPromptModal}>
                  <FileText className="w-4 h-4" />
                  Modifica system prompt
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
